import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import { generatePath, useHistory, useParams } from "react-router-dom";
import Popup from "reactjs-popup";

import { MiniTag } from "@/components/Elements";
import Loading from "@/components/Loading";
import ModuleTableColumn from "@/components/table/shared/ModuleTableColumn";
import { useMainStore } from "@/contexts/Store";
import {
  PARTNER_ASSESSMENT_TEMPLATE_HEADERS as FIELDS,
  RISK_ASSESSMENT_STATUS_DISPLAY,
  ROUTES,
} from "@/features/risk-assessment";
import { AssessmentIndex } from "@/features/risk-assessment/types/assessment";
import { getCountStringOfItem } from "@/utils/utils";

import { AssessmentAPI } from "../../../api/legacy/risk-assessment/AssessmentApi";
import { PartnersAPI } from "../../../api/legacy/risk-assessment/PartnersApi";
import ViewModuleUsers from "../../../components/dashboard/ViewModuleUsers";
import DashboardContent from "../../../components/shared/DashboardContent";
import DashboardContentWrapper from "../../../components/shared/DashboardContentWrapper";
import DashboardHeader from "../../../components/shared/DashboardHeader";
import AssessmentSelectModal from "../components/Assessment/AssessmentSelectModal/AssessmentSelectModal";
import ButtonWithConfirmation from "../components/ButtonWithConfirmation/ButtonWithConfirmation";
import { ModuleContentWrapper } from "../components/ModuleContentWrapper/ModuleContentWrapper";
import ModuleHeader from "../components/ModuleHeader/ModuleHeader";
import PartnerTabs from "../components/PartnerTabs";
import CustomCell from "../components/Table/CustomCell";
import CustomTable from "../components/Table/CustomTable";
import { getPartnerName } from "../utils/partners";

function PartnerAssessmentsPage() {
  const mainStore = useMainStore();
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleteDisabled, setIsDeleteDisabled] = useState(false);
  const [assessments, setAssesments] = useState<Array<AssessmentIndex>>([]);
  const [assessmentTemplates, setAssessmentTemplates] = useState<
    Array<AssessmentIndex>
  >([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const { record_version_id } = useParams<{ record_version_id: string }>();
  const { workspaceID } = mainStore.context;
  const riskMethodologies = mainStore.riskMethodologies.list || [];
  const partnerName = getPartnerName(
    mainStore.riskAssessmentPartners.partners,
    record_version_id,
  );

  useEffect(() => {
    if (!workspaceID) {
      return;
    }
    Promise.all([
      AssessmentAPI.getAll(workspaceID, { partner_id: record_version_id }),
      AssessmentAPI.getAll(workspaceID, { templates: true }),
    ])
      .then(([assessmentsData, assessmentTemplatesData]) => {
        setAssesments(assessmentsData);
        setAssessmentTemplates(assessmentTemplatesData);
        setIsLoading(false);
      })
      .catch((error) => {
        mainStore.toast.setErrorFromResponse(
          error,
          "There was an issue loading Assessments. Please try again",
        );
        history.replace(
          generatePath(ROUTES.PARTNERS_PATH, {
            workspace_id: Number(workspaceID),
          }),
        );
      });
  }, []);

  const attachTemplate = async (
    templateId: number,
    isPasswordProtected: boolean,
  ) => {
    if (!workspaceID) {
      return;
    }
    const data = await PartnersAPI.attachAssessmentTemplate(
      workspaceID,
      record_version_id,
      templateId,
      isPasswordProtected,
    );
    setShowTemplates(false);
    history.push(
      generatePath(ROUTES.ASSESSMENTS_DETAIL_PATH, {
        assessmentId: data.id,
        workspace_id: Number(workspaceID),
      }),
    );
  };

  const deletePartnerAssessment = async (templateId: number) => {
    setIsDeleteDisabled(true);
    try {
      if (workspaceID) {
        await AssessmentAPI.delete(workspaceID, templateId);
        setAssesments((prev) =>
          prev.filter((assessment) => assessment.id !== templateId),
        );
      }
    } catch {
      mainStore.toast.setErrorText(
        "An error has occurred. Please try again later.",
      );
    }
    setIsDeleteDisabled(false);
  };

  const headers = FIELDS.map((field) => (
    <ModuleTableColumn key={field.name} field={field} />
  ));

  const rows = assessments?.map((assessment) => (
    <div key={assessment.id} className="list-table">
      <ul>
        <div className="list-table-wrap">
          <div
            className="list-table-block"
            onClick={() =>
              history.push(
                generatePath(ROUTES.ASSESSMENTS_DETAIL_PATH, {
                  assessmentId: assessment.id,
                  workspace_id: Number(workspaceID),
                }),
              )
            }
          >
            <CustomCell width={FIELDS[0].width}>
              <MiniTag
                label={RISK_ASSESSMENT_STATUS_DISPLAY[assessment.status].text}
                theme={RISK_ASSESSMENT_STATUS_DISPLAY[assessment.status].theme}
              />
            </CustomCell>
            <CustomCell
              width={FIELDS[1].width}
              className="risk-area-table-listing__name"
            >
              {assessment.name}
            </CustomCell>
            <CustomCell width={FIELDS[2].width}>
              {getCountStringOfItem(
                assessment.questionnaire_ids.length,
                "Questionnaire",
              )}
            </CustomCell>
            <CustomCell width={FIELDS[3].width}>
              {
                riskMethodologies.find(
                  (rm) => rm.id == assessment.risk_methodology_id,
                )?.name
              }
            </CustomCell>
            <CustomCell width={FIELDS[3].width} allowClicks>
              <ButtonWithConfirmation
                buttonLabel="Delete"
                onConfirm={() => deletePartnerAssessment(assessment.id)}
                isDisabled={isDeleteDisabled}
                confirmationHeading="Partner Assesment Removal"
                confirmationContent="The partner assessment will be removed and the collected information will be gone after the removal."
              />
            </CustomCell>
            <span className="stretch-cell" />
          </div>
        </div>
      </ul>
    </div>
  ));

  return (
    <DashboardContent>
      <DashboardHeader
        title={`${partnerName} Assessments`.trim()}
        LeftActionBar={<ViewModuleUsers />}
        onBackClick={() =>
          history.push(
            generatePath(ROUTES.PARTNERS_PATH, {
              workspace_id: Number(workspaceID),
            }),
          )
        }
      />
      <DashboardContentWrapper>
        <ModuleHeader>
          <PartnerTabs recordVersionId={record_version_id} />
        </ModuleHeader>
        <ModuleContentWrapper>
          {isLoading && (
            <Loading loadingLayout="table" showTableHeader={false} fullWidth />
          )}
          {!isLoading && (
            <>
              <CustomTable
                headers={headers}
                rows={rows}
                createNewRecord={() => setShowTemplates(true)}
                addNewTemplateString="Add New Assessment +"
              />
              <Popup open={showTemplates} closeOnDocumentClick={false}>
                <AssessmentSelectModal
                  templates={assessmentTemplates}
                  onCancel={() => setShowTemplates(false)}
                  onSubmit={attachTemplate}
                />
              </Popup>
            </>
          )}
        </ModuleContentWrapper>
      </DashboardContentWrapper>
    </DashboardContent>
  );
}

export default observer(PartnerAssessmentsPage);
